<template>
  <div
    ref="wrapperRef"
    class="priority-select-wrapper"
    :class="{ 'is-open': isOpen, 'is-disabled': disabled }"
  >
    <div
      ref="triggerRef"
      class="priority-select-trigger"
      :class="{ 'is-disabled': disabled }"
      @click="toggleDropdown"
    >
      <span v-if="selectedOption" class="priority-select-value">
        {{ selectedOption.label }}
      </span>
      <span v-else class="priority-select-placeholder">{{ placeholder }}</span>
      <span class="priority-select-arrow">▼</span>
    </div>
    <Teleport to="body">
      <div v-if="isOpen" ref="dropdownRef" class="priority-select-dropdown" :style="dropdownStyle">
        <div
          v-for="option in options"
          :key="option.value"
          class="priority-select-option"
          :class="{ 'is-selected': String(selectedValue) === String(option.value) }"
          @click="selectOption(option)"
        >
          {{ option.label }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'

const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  options: {
    type: Array,
    required: true,
    default: () => [],
  },
  placeholder: { type: String, default: 'Select priority' },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const selectedValue = computed(() => props.modelValue)
const wrapperRef = ref(null)
const triggerRef = ref(null)
const dropdownRef = ref(null)
const dropdownStyle = ref({})

const selectedOption = computed(() => {
  if (
    selectedValue.value === null ||
    selectedValue.value === undefined ||
    selectedValue.value === ''
  )
    return null
  return props.options.find(opt => String(opt.value) === String(selectedValue.value)) || null
})

function updateDropdownPosition() {
  if (!isOpen.value || !triggerRef.value) return

  nextTick(() => {
    if (!triggerRef.value) return

    const rect = triggerRef.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const dropdownMaxHeight = 200 // max-height from CSS
    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top

    // For position: fixed, use viewport coordinates directly (no scroll offset needed)
    // Position dropdown above trigger if there's not enough space below
    const shouldPositionAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow

    dropdownStyle.value = {
      position: 'fixed',
      top: shouldPositionAbove ? `${rect.top - dropdownMaxHeight}px` : `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 10000,
      maxHeight: shouldPositionAbove
        ? `${Math.min(dropdownMaxHeight, spaceAbove - 10)}px`
        : `${Math.min(dropdownMaxHeight, spaceBelow - 10)}px`,
    }
  })
}

function toggleDropdown() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    updateDropdownPosition()
  }
}

function selectOption(option) {
  emit('update:modelValue', option.value)
  isOpen.value = false
}

function handleClickOutside(event) {
  if (!isOpen.value) return
  if (wrapperRef.value && wrapperRef.value.contains(event.target)) return
  if (dropdownRef.value && dropdownRef.value.contains(event.target)) return
  isOpen.value = false
}

function handleScroll() {
  if (isOpen.value) {
    updateDropdownPosition()
  }
}

function handleResize() {
  if (isOpen.value) {
    updateDropdownPosition()
  }
}

watch(isOpen, newValue => {
  if (newValue) {
    updateDropdownPosition()
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)
  } else {
    window.removeEventListener('scroll', handleScroll, true)
    window.removeEventListener('resize', handleResize)
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleScroll, true)
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.priority-select-wrapper {
  position: relative;
  width: 100%;
}

.priority-select-trigger {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 38px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.priority-select-trigger:hover:not(.is-disabled) {
  border-color: #999;
}

.priority-select-trigger.is-disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.priority-select-wrapper.is-open .priority-select-trigger {
  border-color: #1d6bf3;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.priority-select-value {
  flex: 1;
  text-align: left;
}

.priority-select-placeholder {
  color: #999;
  flex: 1;
}

.priority-select-arrow {
  font-size: 10px;
  color: #666;
  margin-left: 8px;
  transition: transform 0.2s;
}

.priority-select-wrapper.is-open .priority-select-arrow {
  transform: rotate(180deg);
}

.priority-select-dropdown {
  background: white;
  border: 1px solid #1d6bf3;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.priority-select-option {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.priority-select-option:last-child {
  border-bottom: none;
}

.priority-select-option:hover {
  background-color: #f8f9fa;
}

.priority-select-option.is-selected {
  background-color: #e3f2fd;
}
</style>
